import { Grid } from '@mui/material';
import MultistepForm from 'app/components/MultistepForm';
import { Formik } from 'formik';

import { Gateway } from 'index';
import { omit, omitBy, pick } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { validateForm } from 'utils/FormErrorHelper';
import { EventInformation } from './EventInformation';
import { ioEventValidationSchema } from './validation';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { useMutation } from '@apollo/client';
import { translations } from 'locales/i18n';

import CREATE_MUTATION from 'gql/mutations/InternationalOffice/addIOEvent.gql';
import UPDATE_MUTATION from 'gql/mutations/InternationalOffice/updateIOEvent.gql';

interface Props {
  closeHandler: Function;
  setDisabledCancel: Function;
  event?: any;
}

export function IOEvent(props: Props) {
  const [submitting, setSubmitting] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [createIOEvent] = useMutation(CREATE_MUTATION);
  const [updateIOEvent] = useMutation(UPDATE_MUTATION);

  const initialValues = () => {
    return props.event
      ? {
          ...pick(props.event, [
            'id',
            'eventName',
            'eventDate',
            'eventOfficer',
            'eventOfficerEmail',
            'meetingPoint',
            'registrationDeadline',
            'participants',
            'eventDescription',
            'costs'
          ])
        }
      : {};
  };

  const handleSaveClick = async values => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    if (props.setDisabledCancel) {
      props.setDisabledCancel(true);
    }
    const omitFields = ['id', 'orderNumber'];
    let saveBody: any = {
      ...omit(values, omitFields),
      costs: values.costs
    };

    if (
      values.eventDescription !== undefined &&
      props.event?.eventDescription !== values.eventDescription
    ) {
      if (values.eventDescription === null) {
        saveBody.eventDescription = null;
      } else {
        const formData = new FormData();
        formData.append('file', values.eventDescription);
        try {
          const { data: fileURI } = await Gateway.post('/upload/io/files/events', formData);
          saveBody.eventDescription = fileURI;
        } catch (err) {
          dispatch(
            AlertActions.error(
              t(
                translations.pages.internationalOffice.events.alerts.error.savingCourseDescription()
              )
            )
          );
          props.closeHandler();
          return;
        }
      }
    }

    if (values.id) {
      //* UPDATE IO-EVENT
      try {
        await updateIOEvent({
          variables: {
            id: values.id,
            data: {
              ...omitBy(saveBody, v => v === null || v === ''),
              eventDescription: saveBody.eventDescription
            }
          },
          refetchQueries: ['GetIOEvents']
        });
        dispatch(
          AlertActions.success(
            t(translations.pages.internationalOffice.events.alerts.success.updatedEvent())
          )
        );
        setSubmitting(false);
        props.closeHandler();
      } catch (err) {
        dispatch(
          AlertActions.error(
            t(translations.pages.internationalOffice.events.alerts.error.updatingEvent())
          )
        );
        setSubmitting(false);
      }
    } else {
      //* CREATE IO-EVENT
      try {
        await createIOEvent({
          variables: { data: omitBy(saveBody, v => v === null || v === '') },
          refetchQueries: ['GetIOEvents']
        });
        dispatch(
          AlertActions.success(
            t(translations.pages.internationalOffice.events.alerts.success.createdEvent())
          )
        );
        setSubmitting(false);
        props.closeHandler();
      } catch (err) {
        dispatch(
          AlertActions.error(
            t(translations.pages.internationalOffice.events.alerts.error.creatingEvent())
          )
        );
        setSubmitting(false);
      }
    }
  };

  const steps = ['Event Creation'];

  const EventModalStep = ({ step }) => {
    switch (step) {
      case 0:
        return <EventInformation />;
      default:
        return null;
    }
  };

  return (
    <Grid item className="ioEventModal">
      <Formik
        isInitialValid={!!props.event}
        initialValues={initialValues()}
        onSubmit={values => handleSaveClick(values)}
        validationSchema={ioEventValidationSchema(t)}
        validate={values => {
          return validateForm(values, ioEventValidationSchema(t), dispatch);
        }}
      >
        <MultistepForm
          submitting={submitting}
          steps={steps}
          onCancel={props.closeHandler}
          hideStepper
        >
          {activeStep => (
            <Grid container>
              <EventModalStep step={activeStep} />
            </Grid>
          )}
        </MultistepForm>
      </Formik>
    </Grid>
  );
}
